import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import LinkedInLoginButton from '../components/auth/LinkedInLoginButton';
import { toast } from 'react-toastify';

const ExtensionAuth = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, login, register, googleLogin, linkedinLogin } = useAuth();
  const [action, setAction] = useState('login');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    fullName: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Get action from URL params
    const params = new URLSearchParams(location.search);
    const actionParam = params.get('action');
    if (actionParam === 'register') {
      setAction('register');
    }
    
    // Handle LinkedIn callback
    const code = params.get('code');
    const state = params.get('state');
    const storedState = sessionStorage.getItem('linkedinState');
    
    if (code && state && state === storedState) {
      sessionStorage.removeItem('linkedinState');
      handleLinkedInCallback(code);
    }
  }, [location]);

  // Separate effect to handle authentication
  useEffect(() => {
    if (currentUser) {
      sendAuthToExtension();
    }
  }, [currentUser]);

  const sendAuthToExtension = () => {
    // Send message via postMessage to be picked up by extension bridge
    window.postMessage({
      type: 'RESUMEMO_AUTH_SUCCESS',
      token: localStorage.getItem('token'),
      email: currentUser.email,
      fullName: currentUser.full_name
    }, window.location.origin);
    
    // Show success message and redirect
    setTimeout(() => {
      navigate('/jobs');
    }, 2000);
  };
  
  const handleLinkedInCallback = async (code) => {
    try {
      setLoading(true);
      setError('');
      
      const result = await linkedinLogin(code);
      
      if (result.success) {
        // Wait a bit for currentUser to be set, then send auth
        setTimeout(() => {
          if (currentUser) {
            sendAuthToExtension();
          }
        }, 100);
      } else if (result.needsRegistration && result.email) {
        setAction('register');
        setFormData(prev => ({
          ...prev,
          email: result.email,
          fullName: result.name || ''
        }));
        toast.info('Please complete your registration');
      } else {
        setError('LinkedIn login failed. Please try again.');
      }
    } catch (err) {
      setError('Failed to log in with LinkedIn. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      let success = false;
      if (action === 'login') {
        success = await login(formData.email, formData.password);
      } else {
        success = await register({
          email: formData.email,
          password: formData.password,
          full_name: formData.fullName
        });
        if (success) {
          // For registration, we need to login after successful registration
          success = await login(formData.email, formData.password);
        }
      }
      
      if (success && currentUser) {
        sendAuthToExtension();
      }
    } catch (err) {
      setError(err.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (currentUser) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
              <h2 className="mt-4 text-lg font-medium text-gray-900">Successfully Authenticated!</h2>
              <p className="mt-2 text-sm text-gray-600">
                You are now logged in to ResuMemo. The extension has been updated with your credentials.
              </p>
              <p className="mt-2 text-sm text-gray-500">
                Redirecting to dashboard...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <img className="h-12 w-auto" src="/icon256.png" alt="ResuMemo" />
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          {action === 'login' ? 'Sign in to ResuMemo' : 'Create your ResuMemo account'}
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Extension Authentication
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {action === 'register' && (
              <div>
                <label htmlFor="fullName" className="block text-sm font-medium text-gray-700">
                  Full Name
                </label>
                <div className="mt-1">
                  <input
                    id="fullName"
                    name="fullName"
                    type="text"
                    required
                    value={formData.fullName}
                    onChange={handleInputChange}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                </div>
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="mt-1">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleInputChange}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                />
              </div>
            </div>

            {error && (
              <div className="rounded-md bg-red-50 p-4">
                <div className="text-sm text-red-700">{error}</div>
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                {loading ? 'Processing...' : (action === 'login' ? 'Sign in' : 'Create account')}
              </button>
            </div>
          </form>

          {/* Divider */}
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Or continue with</span>
              </div>
            </div>
          </div>
          
          {/* Social Login */}
          <div className="mt-6 space-y-3">
            <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
              <div className="flex justify-center">
                <GoogleLogin
                  onSuccess={async (credentialResponse) => {
                    try {
                      setLoading(true);
                      const result = await googleLogin(credentialResponse.credential);
                      
                      if (result.success) {
                        // Wait a bit for currentUser to be set, then send auth
                        setTimeout(() => {
                          if (currentUser) {
                            sendAuthToExtension();
                          }
                        }, 100);
                      } else if (result.needsRegistration && result.email) {
                        setAction('register');
                        setFormData(prev => ({
                          ...prev,
                          email: result.email,
                          fullName: result.name || ''
                        }));
                        toast.info('Please complete your registration');
                      }
                    } catch (err) {
                      setError('Failed to log in with Google. Please try again.');
                    } finally {
                      setLoading(false);
                    }
                  }}
                  onError={() => {
                    setError('Google login failed. Please try again.');
                  }}
                  theme="outline"
                  text="signin_with"
                  size="large"
                  width="100%"
                  logo_alignment="center"
                  style={{ width: '100%' }}
                />
              </div>
            </GoogleOAuthProvider>
            
            <div className="flex justify-center">
              <LinkedInLoginButton square={true} />
            </div>
          </div>
          
          <div className="mt-6">
            <div className="text-center">
              <button
                onClick={() => setAction(action === 'login' ? 'register' : 'login')}
                className="text-sm text-primary-600 hover:text-primary-500"
              >
                {action === 'login' 
                  ? "Don't have an account? Sign up" 
                  : "Already have an account? Sign in"
                }
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExtensionAuth;